/*
 * ---------------------------------------------------------------------------
 * COMMERCIAL IN CONFIDENCE
 *
 * (c) Copyright Quosient Ltd. All Rights Reserved.
 *
 * See LICENSE.txt in the repository root.
 * ---------------------------------------------------------------------------
 */

/*
* Add new radar layer to the map
* User can select which polarization to visualize 
*/
// Issues:
// - speckle filtering?

import * as Blockly from 'blockly/core';
import 'blockly/python';
import { getBandArray, getCurrentSatUsingRootBlock } from '../dataset';
import { getAllowedOutputs } from '../block_compatibility_checks';
import { getIndexOfValueInDropdownOptions, hasSatChanged, updateBlockSat } from '../helper_functions';

Blockly.Blocks['output_radar_vis'] = {
    init: function () {
        // generate all options for block before it starts
        let options = this.generateBandOptions()
        console.log("init function options", options)

        this.appendDummyInput('bandSelectionInput')
            .appendField("Radar data visualisation. ")
            .appendField("R ", "r_text")
            .appendField(new Blockly.FieldDropdown(options, this.validateRadarVisDropdown), "r")
            .appendField(" G ", "g_text")
            .appendField(new Blockly.FieldDropdown(options, this.validateRadarVisDropdown), "g")
            .appendField(" B ", "b_text")
            .appendField(new Blockly.FieldDropdown(options, this.validateRadarVisDropdown), "b")
        this.appendDummyInput('compositeDummyInput')
            .appendField("Composite: ")
            .appendField(new Blockly.FieldDropdown([["Mean", ".mean()"], ["Median", ".median()"], ["Min", ".min()"], ['Max', '.max()']]), "param")
            .appendField("  Layer name:  ")
            .appendField(new Blockly.FieldTextInput("default", this.layerNameValidator), "layerName");

        this.getField("layerName").setTooltip(Blockly.Msg.LAYER_NAME_TOOLTIP)

        this.setPreviousStatement(true, 'radar_vis');
        this.setNextStatement(true, getAllowedOutputs('radar_vis'));
        this.setStyle('mapVisualisation');
        this.setTooltip(Blockly.Msg.RADAR_VIS_BLOCK_TOOLTIP);
        this.setHelpUrl("");
    },

    validateLayerName(newValue) {
        let maxLength = 20;
        let cleanValue = newValue.replace(/[^a-zA-Z0-9 ]/g, "");
        let trimmedValue = cleanValue.substring(0, maxLength)
        return trimmedValue;
    },

    generateBandOptions() {
        let sat = getCurrentSatUsingRootBlock(this);
        if ((sat !== null) && (sat !== '')) {
            return getBandArray[sat]();
        } else {
            return getBandArray['SAR']();
        }
    },

    updateBandDropdowns(){
        // get old input values for bands
        let oldR = this.getFieldValue('r');
        let oldG = this.getFieldValue('g');
        let oldB = this.getFieldValue('b');

        this.removeInput('bandSelectionInput');

        let bandOptions = this.generateBandOptions();

        this.appendDummyInput('bandSelectionInput')
            .appendField("Radar data visualisation. ")
            .appendField("R ", "r_text")
            .appendField(new Blockly.FieldDropdown(bandOptions, this.validateRadarVisDropdown), "r")
            .appendField(" G ", "g_text")
            .appendField(new Blockly.FieldDropdown(bandOptions, this.validateRadarVisDropdown), "g")
            .appendField(" B ", "b_text")
            .appendField(new Blockly.FieldDropdown(bandOptions, this.validateRadarVisDropdown), "b")
            
        this.moveInputBefore("bandSelectionInput", "compositeDummyInput");

        let defaultValue = this.sat === 'PALSAR' ? 'HV' : "VV" 

        // check if old bands are still in band array, if so select them if not use the default value
        let newR = getIndexOfValueInDropdownOptions(oldR, bandOptions) === -1 ? defaultValue : oldR;
        let newG = getIndexOfValueInDropdownOptions(oldG, bandOptions) === -1 ? defaultValue : oldG;
        let newB = getIndexOfValueInDropdownOptions(oldB, bandOptions) === -1 ? defaultValue : oldB;

        // update fields to use default values for that sat
        // and force update to field to call validator
        this.setFieldValue(newR, 'r');
        this.setFieldValue(newG, 'g');
        this.setFieldValue(newB, 'b');
    },

    onchange: function(event) {
        // adding checks for the end of a drag event i.e. a block has been moved somewhere
        // e.g. outside of its parent block, change events pick up whenever something
        // in the entire block hierarchy has changed e.g. the user changes a dropdown in the parent
        // block
        // DO NOT ALLOW event.type == 'move' THIS WILL CAUSE HEADACHES
        // event.type == 'create' is also quite janky
        // using finished_loading event to capture when user loads workflow or when a featuredWorkflow is clicked
        if((event.type == 'drag' && !event.isStart) || event.type == 'change' || event.type == 'finished_loading'){
            updateBlockSat(this);
            if (hasSatChanged(this) || event.type == 'finished_loading') {
                this.updateBandDropdowns();
            }
        }
    },

    bands: null,

    rgbBands: {
        'r': null,
        'g': null,
        'b': null
    },

    validateRadarVisDropdown(newValue) {
        let sourceBlock = this.getSourceBlock();
        // band is the field name e.g. 'r'
        let band = this.name;

        sourceBlock.updateBands(band, newValue);
        return newValue
    },

    updateBands(band, polarisation) {
        this.rgbBands[band] = polarisation;
        // get all bands as array
        let allBands = Object.values(this.rgbBands);
        // create a set (where all values must be unique and convert to array)
        let uniqueBands = [... new Set(allBands)]
        // map unqiue bands to blockly menu format i.e. Array of tuples
        this.bands = uniqueBands.map((band) => [band, band])
    }
}